import React, { useEffect, useState } from 'react'
import './StorySelection'
import './StoryReader'
import '../styles/kidscatchup.css'

type View = 'home' | 'story' | 'teacher' | 'breakthrough-home' | 'breakthrough-exercises'

// Import components dynamically to avoid circular dependencies
const StorySelection = React.lazy(() => import('./StorySelection'))
const StoryReader = React.lazy(() => import('./StoryReader'))
const TeacherDashboard = React.lazy(() => import('./TeacherDashboard'))
const BreakthroughSelection = React.lazy(() => import('./BreakthroughSelection'))
const SightWordExercises = React.lazy(() => import('./SightWordExercises'))

// Story sequence based on book order
const STORY_SEQUENCE = ['C', 'A', 'D', 'S', 'I', 'R', 'N', 'M', 'T', 'L', 'B', 'O', 'H', 'E', 'K', 'U', 'V', 'J', 'P', 'G', 'F', 'Y', 'W', 'X', 'Q', 'Z']

export default function App() {
  const [status, setStatus] = useState<string>('loading...')
  const [currentView, setCurrentView] = useState<View>('home')
  const [selectedStoryId, setSelectedStoryId] = useState<string | null>(null)
  const [unlockedStories, setUnlockedStories] = useState<string[]>(['C']) // Start with story C unlocked
  const [showTeacherMode, setShowTeacherMode] = useState(false)
  const [currentProgramme, setCurrentProgramme] = useState<'kidscatchup' | 'breakthrough'>('kidscatchup')
  const [breakthroughUnlockedSets, setBreakthroughUnlockedSets] = useState<string[]>(['set1']) // Start with first set unlocked
  const [selectedBreakthroughSetId, setSelectedBreakthroughSetId] = useState<string | null>(null)

  useEffect(() => {
    fetch('/health')
      .then(r => r.json())
      .then(j => setStatus(`System: ${j.status.toUpperCase()}`))
      .catch(() => setStatus('System: OFFLINE'))
  }, [])

  const handleSelectStory = (storyId: string) => {
    setSelectedStoryId(storyId)
    setCurrentView('story')
  }

  const handleBackToStories = () => {
    setCurrentView('home')
    setSelectedStoryId(null)
  }

  const handleStoryComplete = (storyId: string) => {
    // Unlock the next story in the sequence
    const currentIndex = STORY_SEQUENCE.indexOf(storyId)
    if (currentIndex !== -1 && currentIndex < STORY_SEQUENCE.length - 1) {
      const nextStory = STORY_SEQUENCE[currentIndex + 1]
      setUnlockedStories(prev => {
        if (!prev.includes(nextStory)) {
          return [...prev, nextStory]
        }
        return prev
      })
    }
  }

  const handleSelectBreakthroughSet = (setId: string) => {
    setSelectedBreakthroughSetId(setId)
    setCurrentView('breakthrough-exercises')
  }

  const handleBackToBreakthroughSets = () => {
    setCurrentView('breakthrough-home')
    setSelectedBreakthroughSetId(null)
  }

  const handleBreakthroughSetComplete = () => {
    // Unlock the next breakthrough set
    if (selectedBreakthroughSetId) {
      const setNumber = parseInt(selectedBreakthroughSetId.replace('set', ''))
      const nextSet = `set${setNumber + 1}`
      setBreakthroughUnlockedSets(prev => {
        if (!prev.includes(nextSet)) {
          return [...prev, nextSet]
        }
        return prev
      })
    }
  }

  const switchProgramme = (programme: 'kidscatchup' | 'breakthrough') => {
    setCurrentProgramme(programme)
    if (programme === 'kidscatchup') {
      setCurrentView('home')
      setSelectedBreakthroughSetId(null)
    } else {
      setCurrentView('breakthrough-home')
      setSelectedStoryId(null)
    }
    setShowTeacherMode(false)
  }

  return (
    <div style={{ width: '100%', minHeight: '100vh', margin: 0, padding: 0 }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '1.5rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold' }}>
            📚 KidsCatchUp
          </h1>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
            Interactive Reading Programme
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{
            padding: '0.5rem 1rem',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: 'bold'
          }}>
            {status}
          </div>

          {/* Programme Switcher */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            background: 'rgba(255, 255, 255, 0.15)',
            padding: '0.3rem',
            borderRadius: '10px'
          }}>
            <button
              onClick={() => switchProgramme('kidscatchup')}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                background: currentProgramme === 'kidscatchup' ? 'white' : 'transparent',
                color: currentProgramme === 'kidscatchup' ? '#667eea' : 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              📚 Kids CatchUp
            </button>
            <button
              onClick={() => switchProgramme('breakthrough')}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                background: currentProgramme === 'breakthrough' ? 'white' : 'transparent',
                color: currentProgramme === 'breakthrough' ? '#667eea' : 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              🎯 Breakthrough
            </button>
          </div>

          <button
            onClick={() => {
              if (showTeacherMode) {
                setCurrentView(currentProgramme === 'breakthrough' ? 'breakthrough-home' : 'home')
                setShowTeacherMode(false)
              } else {
                setCurrentView('teacher')
                setShowTeacherMode(true)
              }
            }}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 'bold',
              background: 'white',
              color: '#667eea',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(255, 255, 255, 0.3)',
              transition: 'all 0.3s ease'
            }}
          >
            {showTeacherMode ? '📚 Back to Student View' : '👩‍🏫 Teacher Dashboard'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ width: '100%' }}>
        {currentView === 'home' && (
          <React.Suspense fallback={<div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            fontSize: '1.5rem',
            color: '#667eea'
          }}>
            📖 Loading stories...
          </div>}>
            <StorySelection
              unlockedStories={unlockedStories}
              onSelectStory={handleSelectStory}
            />
          </React.Suspense>
        )}

        {currentView === 'story' && selectedStoryId && (
          <React.Suspense fallback={<div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            fontSize: '1.5rem',
            color: '#667eea'
          }}>
            📖 Loading story...
          </div>}>
            <StoryReader
              storyId={selectedStoryId}
              onBack={handleBackToStories}
              onComplete={handleStoryComplete}
              unlockedStories={unlockedStories}
            />
          </React.Suspense>
        )}

        {currentView === 'breakthrough-home' && (
          <React.Suspense fallback={<div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            fontSize: '1.5rem',
            color: '#667eea'
          }}>
            📖 Loading Breakthrough Programme...
          </div>}>
            <BreakthroughSelection
              unlockedSets={breakthroughUnlockedSets}
              onSelectSet={handleSelectBreakthroughSet}
            />
          </React.Suspense>
        )}

        {currentView === 'breakthrough-exercises' && selectedBreakthroughSetId && (
          <React.Suspense fallback={<div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            fontSize: '1.5rem',
            color: '#667eea'
          }}>
            📖 Loading exercises...
          </div>}>
            <SightWordExercises
              setId={selectedBreakthroughSetId}
              onBack={handleBackToBreakthroughSets}
              onComplete={handleBreakthroughSetComplete}
            />
          </React.Suspense>
        )}

        {currentView === 'teacher' && (
          <React.Suspense fallback={<div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            fontSize: '1.5rem',
            color: '#667eea'
          }}>
            📊 Loading teacher dashboard...
          </div>}>
            <TeacherDashboard
              onClose={() => {
                setCurrentView(currentProgramme === 'breakthrough' ? 'breakthrough-home' : 'home')
                setShowTeacherMode(false)
              }}
              unlockedStories={currentProgramme === 'breakthrough' ? breakthroughUnlockedSets : unlockedStories}
            />
          </React.Suspense>
        )}
      </div>
    </div>
  )
}
